'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import { FlockRecord } from './flockData';
import { Loader2 } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (data: Partial<FlockRecord>) => void;
}

interface FormData {
  flockName: string;
  birdType: string;
  breed: string;
  purpose: string;
  farm: string;
  house: string;
  supplier: string;
  arrivalDate: string;
  initialQty: number;
  costPerChick: number;
  expectedCycleDays: number;
  targetMarketDate: string;
  source: string;
  notes: string;
}

const BIRD_TYPES = ['Broiler', 'Layer', 'Breeder', 'Local/Indigenous', 'Other'];
const FARMS = ['Idudi Poultry Farm', 'Kampala Demo Farm'];
const HOUSES = ['House A1', 'House A2', 'House B1', 'House C1', 'House D2', 'House E1', 'Layer House 01', 'Layer House 02', 'Layer House 03', 'Breeder House 01'];
const PURPOSES = ['Meat', 'Eggs', 'Hatching Eggs', 'Meat & Eggs', 'Breeding'];
const SOURCES = ['Hatchery', 'Purchase', 'Transfer In', 'Own Breeding'];

export default function CreateFlockModal({ open, onClose, onCreate }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: {
      birdType: 'Broiler',
      farm: 'Idudi Poultry Farm',
      house: 'House A1',
      purpose: 'Meat',
      source: 'Hatchery',
      expectedCycleDays: 42,
      costPerChick: 2800,
    },
  });

  function onSubmit(data: FormData) {
    setSubmitting(true);
    // Backend: POST /api/flocks
    setTimeout(() => {
      onCreate({
        ...data,
        initialQty: Number(data.initialQty),
        costPerChick: Number(data.costPerChick),
        expectedCycleDays: Number(data.expectedCycleDays),
      });
      reset();
      setSubmitting(false);
    }, 800);
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create New Flock"
      subtitle="Register a new flock batch in the system"
      size="lg"
      footer={
        <>
          <button onClick={handleClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={submitting}
            className="btn-primary text-sm"
            style={{ minWidth: '120px' }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Creating...
              </>
            ) : (
              'Create Flock'
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Section: Basic Info */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Flock Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Flock Name */}
            <div>
              <label htmlFor="cf-name" className="block text-sm font-medium text-foreground mb-1">
                Flock Name <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">A descriptive name for this batch</p>
              <input
                id="cf-name"
                type="text"
                className="input-field"
                placeholder="e.g. Batch A3 — Cobb 500"
                {...register('flockName', { required: 'Flock name is required' })}
              />
              {errors.flockName && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.flockName.message}</p>}
            </div>

            {/* Bird Type */}
            <div>
              <label htmlFor="cf-type" className="block text-sm font-medium text-foreground mb-1">
                Bird Type <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Select the production category</p>
              <div className="relative">
                <select id="cf-type" className="select-field" {...register('birdType', { required: true })}>
                  {BIRD_TYPES.map((t) => <option key={`btype-${t}`} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Breed */}
            <div>
              <label htmlFor="cf-breed" className="block text-sm font-medium text-foreground mb-1">
                Breed <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="cf-breed"
                type="text"
                className="input-field"
                placeholder="e.g. Cobb 500, Ross 308, Lohmann Brown"
                {...register('breed', { required: 'Breed is required' })}
              />
              {errors.breed && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.breed.message}</p>}
            </div>

            {/* Purpose */}
            <div>
              <label htmlFor="cf-purpose" className="block text-sm font-medium text-foreground mb-1">Production Purpose</label>
              <div className="relative">
                <select id="cf-purpose" className="select-field" {...register('purpose')}>
                  {PURPOSES.map((p) => <option key={`purp-${p}`} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t" style={{ borderColor: 'var(--border)' }} />

        {/* Section: Location */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Farm & House Assignment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cf-farm" className="block text-sm font-medium text-foreground mb-1">
                Farm <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <div className="relative">
                <select id="cf-farm" className="select-field" {...register('farm', { required: true })}>
                  {FARMS.map((f) => <option key={`farm-opt-${f}`} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cf-house" className="block text-sm font-medium text-foreground mb-1">
                Poultry House <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Must be available and not over capacity</p>
              <div className="relative">
                <select id="cf-house" className="select-field" {...register('house', { required: true })}>
                  {HOUSES.map((h) => <option key={`house-opt-${h}`} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t" style={{ borderColor: 'var(--border)' }} />

        {/* Section: Procurement */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Procurement Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cf-supplier" className="block text-sm font-medium text-foreground mb-1">
                Supplier / Hatchery <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="cf-supplier"
                type="text"
                className="input-field"
                placeholder="e.g. Ugachick Hatchery"
                {...register('supplier', { required: 'Supplier is required' })}
              />
              {errors.supplier && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.supplier.message}</p>}
            </div>
            <div>
              <label htmlFor="cf-source" className="block text-sm font-medium text-foreground mb-1">Source</label>
              <div className="relative">
                <select id="cf-source" className="select-field" {...register('source')}>
                  {SOURCES.map((s) => <option key={`src-${s}`} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cf-qty" className="block text-sm font-medium text-foreground mb-1">
                Initial Quantity (birds) <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Number of chicks/birds placed in house</p>
              <input
                id="cf-qty"
                type="number"
                className="input-field"
                placeholder="e.g. 5000"
                {...register('initialQty', {
                  required: 'Initial quantity is required',
                  min: { value: 1, message: 'Must be at least 1 bird' },
                  max: { value: 100000, message: 'Exceeds maximum capacity' },
                })}
              />
              {errors.initialQty && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.initialQty.message}</p>}
            </div>
            <div>
              <label htmlFor="cf-cost" className="block text-sm font-medium text-foreground mb-1">Cost Per Chick (UGX)</label>
              <p className="text-xs text-muted-foreground mb-1.5">Unit purchase price per bird</p>
              <input
                id="cf-cost"
                type="number"
                className="input-field"
                placeholder="e.g. 2800"
                {...register('costPerChick', { min: { value: 0, message: 'Cannot be negative' } })}
              />
              {errors.costPerChick && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.costPerChick.message}</p>}
            </div>
          </div>
        </div>

        <div className="border-t" style={{ borderColor: 'var(--border)' }} />

        {/* Section: Timeline */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Production Timeline</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="cf-arrival" className="block text-sm font-medium text-foreground mb-1">
                Arrival Date <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="cf-arrival"
                type="date"
                className="input-field"
                {...register('arrivalDate', { required: 'Arrival date is required' })}
              />
              {errors.arrivalDate && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.arrivalDate.message}</p>}
            </div>
            <div>
              <label htmlFor="cf-cycle" className="block text-sm font-medium text-foreground mb-1">Expected Cycle (days)</label>
              <p className="text-xs text-muted-foreground mb-1.5">Broilers: 42 days, Layers: 365 days</p>
              <input
                id="cf-cycle"
                type="number"
                className="input-field"
                placeholder="42"
                {...register('expectedCycleDays', { min: { value: 1, message: 'Must be at least 1 day' } })}
              />
            </div>
            <div>
              <label htmlFor="cf-target" className="block text-sm font-medium text-foreground mb-1">Target Market Date</label>
              <input
                id="cf-target"
                type="date"
                className="input-field"
                {...register('targetMarketDate')}
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="cf-notes" className="block text-sm font-medium text-foreground mb-1">Notes / Observations</label>
          <textarea
            id="cf-notes"
            rows={3}
            className="input-field"
            placeholder="Any additional notes about this flock batch..."
            {...register('notes')}
          />
        </div>

        {/* Required fields note */}
        <p className="text-xs text-muted-foreground">
          Fields marked <span style={{ color: 'var(--danger)' }}>*</span> are required.
        </p>
      </form>
    </Modal>
  );
}